// Quick Call System - Direct Twilio Integration
import React, { useState } from 'react';
import { getApiUrl } from '../../utils/apiConfig';
import { Phone, MessageSquare, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

interface Vendor {
  name: string;
  phone: string;
  company: string;
}

const QuickCallSystem: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });

  // Predefined vendors for quick testing
  const vendors: Vendor[] = [
    { name: 'John Smith', phone: '+14045551234', company: 'Smith Plumbing' },
    { name: 'Sarah Johnson', phone: '+14045552345', company: 'Johnson Electric' },
    { name: 'Mike Williams', phone: '+14045553456', company: 'Williams Roofing' },
  ];

  const makeCall = async (to: string = phoneNumber, vendor: string = vendorName) => {
    setIsLoading(true);
    setStatus({ type: null, message: '' });
    
    try {
      const apiUrl = getApiUrl();
      const response = await axios.post(`${apiUrl}/api/test-call`, {
        to: to.startsWith('+') ? to : `+1${to.replace(/\D/g, '')}`,
        vendorName: vendor || 'Vendor',
        callerName: 'John Builder',
        purpose: 'discussing a construction project'
      });

      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: `Call initiated! SID: ${response.data.callSid}` 
        });
      }
    } catch (error: any) {
      setStatus({ 
        type: 'error', 
        message: error.response?.data?.error || 'Failed to make call' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendSMS = async (to: string = phoneNumber, text: string = message) => {
    setIsLoading(true);
    setStatus({ type: null, message: '' });
    
    try {
      const apiUrl = getApiUrl();
      const response = await axios.post(`${apiUrl}/api/test-sms`, {
        to: to.startsWith('+') ? to : `+1${to.replace(/\D/g, '')}`,
        message: text || 'Hello from HomeQuest! Ready to discuss your project.'
      });

      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: `SMS sent! SID: ${response.data.messageSid}` 
        });
      }
    } catch (error: any) {
      setStatus({ 
        type: 'error', 
        message: error.response?.data?.error || 'Failed to send SMS' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8">Quick Call System</h1>
        
        {/* Status Message */}
        {status.type && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            status.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {status.type === 'success' ? <CheckCircle2 /> : <AlertCircle />}
            <span>{status.message}</span>
          </div>
        )}

        {/* Custom Number Input */}
        <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Custom Call/SMS</h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm">Phone Number</label>
              <input
                type="tel"
                placeholder="+1234567890"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full mt-1 px-4 py-2 bg-gray-900/50 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm">Vendor Name</label>
              <input
                type="text"
                placeholder="John Doe"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                className="w-full mt-1 px-4 py-2 bg-gray-900/50 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-gray-400 text-sm">SMS Message (optional)</label>
              <textarea
                placeholder="Your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full mt-1 px-4 py-2 bg-gray-900/50 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => makeCall()}
                disabled={!phoneNumber || isLoading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="animate-spin" /> : <Phone />}
                Make Call
              </button>

              <button
                onClick={() => sendSMS()}
                disabled={!phoneNumber || isLoading}
                className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="animate-spin" /> : <MessageSquare />}
                Send SMS
              </button>
            </div>
          </div>
        </div>

        {/* Quick Vendor List */}
        <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Quick Call Vendors</h2>
          
          <div className="space-y-3">
            {vendors.map((vendor, index) => (
              <div key={index} className="bg-gray-900/50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">{vendor.name}</h3>
                  <p className="text-gray-400 text-sm">{vendor.company}</p>
                  <p className="text-gray-500 text-sm">{vendor.phone}</p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => makeCall(vendor.phone, vendor.name)}
                    disabled={isLoading}
                    className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Phone size={18} />
                  </button>
                  
                  <button
                    onClick={() => sendSMS(vendor.phone, `Hi ${vendor.name}, this is HomeQuest. We'd like to discuss a project with ${vendor.company}.`)}
                    disabled={isLoading}
                    className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Your Twilio Info */}
        <div className="mt-6 bg-gray-800/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">
            <span className="font-semibold">Your Twilio Number:</span> +16783253060
          </p>
          <p className="text-gray-500 text-xs mt-1">
            Calls and SMS will be sent from this number
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickCallSystem;